import registry from "../../../registry.json";
import { siteConfig } from "../site";

const NAMESPACE = `${siteConfig.registryNamespace}/`;

const SOLID1_KOBALTE =
  "@kobalte/core@https://github.com/jhomra21/kobalte/releases/download/kobalte-solid1-audiocn-e0e3bf095f05c7e61230a251b79181c6d834d408/kobalte-core-0.13.14-audiocn.0.e0e3bf09.tgz";

const SOLID2_KOBALTE =
  "@kobalte/core@https://github.com/jhomra21/kobalte/releases/download/kobalte-solid2-audiocn-b394be557e697ad4d3c28210df8a75aa3c300914-bundled.1/kobalte-core-2.0.0-alpha.2-audiocn.2.b394be55.tgz";

export interface RegistryFile {
  path: string;
  type: string;
}

export interface RegistryItem {
  name: string;
  type: string;
  title: string;
  description?: string;
  dependencies?: string[];
  registryDependencies?: string[];
  files: RegistryFile[];
  cssVars?: Record<string, Record<string, string>>;
  categories?: string[];
}

/** What the shadcn CLI writes and installs for one item, resolved transitively. */
export interface InstallPlan {
  runtime: RegistryRuntime;
  /** The item itself. */
  item: RegistryItem;
  /** audiocn items pulled in with it, in the order the registry declares them. */
  registryItems: RegistryItem[];
  /** shadcn/ui items pulled in with it, such as `card`. */
  shadcnItems: string[];
  /** npm packages installed with it. */
  npmDependencies: string[];
  /** CSS custom properties added to the global stylesheet, with their `--` prefix. */
  cssVariables: string[];
}

export type RegistryRuntime = "solid1" | "solid2";

const runtimeDependencies = (
  dependencies: string[] | undefined,
  runtime: RegistryRuntime
): string[] => {
  if (runtime === "solid1")
    return (dependencies ?? []).map((dependency) =>
      dependency.startsWith("@kobalte/core@") ? SOLID1_KOBALTE : dependency
    );

  const resolved = (dependencies ?? []).map((dependency) => {
    if (dependency.startsWith("solid-js@")) return "solid-js@2.0.0-rc.14";

    if (dependency.startsWith("@solidjs/web@"))
      return "@solidjs/web@2.0.0-rc.14";

    if (dependency.startsWith("@kobalte/core@")) return SOLID2_KOBALTE;

    return dependency;
  });

  if (!resolved.some((dependency) => dependency.startsWith("@solidjs/web@")))
    resolved.push("@solidjs/web@2.0.0-rc.14");

  return resolved;
};

const items = registry.items satisfies RegistryItem[];

const byName = new Map(items.map((item) => [item.name, item]));

export const getRegistryItem = (name: string): RegistryItem | undefined =>
  byName.get(name);

export const registryItemUrl = (
  name: string,
  runtime: RegistryRuntime = "solid2"
): string => `${siteConfig.url}/r/${runtime}/${name}.json`;

/**
 * The same item on whichever deployment is being read, for fetching from the
 * browser. The canonical URL redirects to `www`, and a redirect carries no
 * CORS headers, so a cross-origin fetch of it fails.
 */
export const registryItemPath = (
  name: string,
  runtime: RegistryRuntime = "solid2"
): string => new URL(registryItemUrl(name, runtime)).pathname;

/** The file a page's component lives in, such as `components/ui/knob.tsx`. */
export const primaryFile = (item: RegistryItem): string =>
  item.files[0]?.path ?? "";

const EXTENSION = /\.[^./]+$/u;

/** The alias a consumer imports the item from, such as `@/components/ui/knob`. */
export const importPathFor = (item: RegistryItem): string =>
  `@/${primaryFile(item).replace(EXTENSION, "")}`;

const byFile = new Map<string, RegistryItem>(
  items.flatMap((item) =>
    item.files.map(
      (file) => [`@/${file.path.replace(EXTENSION, "")}`, item] as const
    )
  )
);

/** The item an import such as `@/hooks/use-demo-signal` comes from. */
export const itemForImportPath = (path: string): RegistryItem | undefined =>
  byFile.get(path);

/** Docs sections whose last path segment is a registry item name. */
const ITEM_SECTIONS = [
  "/docs/components/",
  "/docs/blocks/",
  "/docs/hooks/",
] as const;

/** The item a docs page documents, if it documents one. */
export const registryItemForPath = (
  pathname: string
): RegistryItem | undefined => {
  if (!ITEM_SECTIONS.some((section) => pathname.startsWith(section))) {
    return undefined;
  }

  return byName.get(pathname.slice(pathname.lastIndexOf("/") + 1));
};

export const resolveInstall = (
  item: RegistryItem,
  runtime: RegistryRuntime = "solid2"
): InstallPlan => {
  const registryItems: RegistryItem[] = [];
  const shadcnItems: string[] = [];
  const npmDependencies: string[] = [];
  const cssVariables: string[] = [];
  const seen = new Set<string>([item.name]);

  const addNpm = (dependency: string) => {
    if (!npmDependencies.includes(dependency)) {
      npmDependencies.push(dependency);
    }
  };

  const addCssVars = (current: RegistryItem) => {
    for (const name of Object.keys(current.cssVars?.light ?? {})) {
      const variable = `--${name}`;

      if (!cssVariables.includes(variable)) {
        cssVariables.push(variable);
      }
    }
  };

  const visit = (current: RegistryItem) => {
    for (const dependency of runtimeDependencies(
      current.dependencies,
      runtime
    )) {
      addNpm(dependency);
    }

    addCssVars(current);

    for (const dependency of current.registryDependencies ?? []) {
      if (!dependency.startsWith(NAMESPACE)) {
        if (!shadcnItems.includes(dependency)) {
          shadcnItems.push(dependency);
        }

        continue;
      }

      const name = dependency.slice(NAMESPACE.length);

      if (seen.has(name)) {
        continue;
      }

      seen.add(name);
      const next = byName.get(name);

      if (!next) {
        throw new Error(`Unknown registry dependency "${dependency}"`);
      }

      registryItems.push(next);
      visit(next);
    }
  };

  visit(item);

  return {
    runtime,
    cssVariables,
    item,
    npmDependencies,
    registryItems,
    shadcnItems,
  };
};
